import { AddIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Trash } from "react-feather";
import { Link } from "react-router-dom";
import { Context, useCurrentProfile } from "../../ContextWrapper";
import {
  createCustomer,
  createCustomerGroup,
  deleteCustomer,
  exportCustomers,
  fetchCustomers,
  fetchGroupCustomers,
  updateCustomer,
  updateCustomerGroup,
} from "../../api/customers";
import CustomTableContainer from "../../theme/components/tableContainer";
import { DropDown } from "../generic/dropDown";
import { LogoField } from "../generic/logoField";
import { GenericModal } from "../generic/modal";
import BulkCalendarComponent from "./bulkCalendarComponent";
import BulkReportStatusSelect from "./bulkReportStatusSelect";
import ClientCalendarComponent from "./clientCalendarComponent";
import { ClientCurrentStatus } from "./clientCurrentStatus";
import { ReportStatusSelect } from "./reportStatusSelect";

// Separate search component to isolate search state
const SearchInput = React.memo(({ onSearchChange }) => {
  const [tempSearch, setTempSearch] = useState("");

  useEffect(() => {
    onSearchChange(tempSearch);
  }, [tempSearch, onSearchChange]);

  return (
    <InputGroup w="340px">
      <Input
        placeholder="Search..."
        value={tempSearch}
        onChange={(e) => setTempSearch(e.target.value)}
      />
      <InputRightElement
        bg="brand.500"
        borderBottomRightRadius={6}
        borderTopRightRadius={6}
      >
        <SearchIcon variant="white" />
      </InputRightElement>
    </InputGroup>
  );
});

// Memoized row component to prevent unnecessary re-renders
const CustomerRow = React.memo(({
  customer,
  isSelected,
  onSelectCustomer,
  onUpdateReportStatus,
  onUpdateDashboardDate,
  onStartAssignClients,
  onStartUpdateClient
}) => {
  return (
    <Tr key={customer.id}>
      <Td>
        <Checkbox
          isChecked={isSelected}
          onChange={() => onSelectCustomer(customer.id)}
        />
      </Td>
      <Td>
        <Box display="inline-flex" alignItems="center">
          <Image
            src={
              customer.logo ||
              `https://ui-avatars.com/api/?size=24&name=${customer.name}`
            }
            alt={customer.name || "Placeholder"}
            boxSize="25px"
            objectFit="cover"
            borderRadius="full"
            mr="2"
          />
          {customer.name}
        </Box>
      </Td>
      <Td>{customer.email}</Td>
      <Td>{customer.primary_contact}</Td>
      <Td>
        <ReportStatusSelect
          customerId={customer.id}
          currentStatus={customer.report_status}
          onUpdate={(newStatus) => onUpdateReportStatus(customer.id, newStatus)}
        />
      </Td>
      <Td>
        <ClientCalendarComponent
          date={
            customer.dashboard_date
              ? new Date(customer.dashboard_date)
              : null
          }
          id={customer.id}
          onDateUpdate={(newDate) => onUpdateDashboardDate(customer.id, newDate)}
        />
      </Td>
      <Td>
        <ClientCurrentStatus customer={customer} />
      </Td>
      <Td py="1">
        <Stack p="0" direction="row" spacing={2} align="center">
          {customer?.customer_type === "group" && (
            <Button
              variant="outline"
              onClick={() => onStartAssignClients(customer)}
            >
              Assign to group
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onStartUpdateClient(customer)}
          >
            Edit
          </Button>
          <Link to={`/client/${customer.id}`}>
            <Button>View</Button>
          </Link>
        </Stack>
      </Td>
    </Tr>
  );
});

export const ClientList = () => {
  const [customerList, setCustomerList] = useState([]);
  const [search, setSearch] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const context = React.useContext(Context);
  const { profile } = useCurrentProfile();
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const {
    isOpen: isClientOpen,
    onOpen: onClientOpen,
    onClose: onClientClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const {
    isOpen: isAssignOpen,
    onOpen: onAssignOpen,
    onClose: onAssignClose,
  } = useDisclosure();

  const [selectedClient, setSelectedClient] = useState(null);

  const handleSearchChange = useCallback((newSearch) => {
    setSearch(newSearch);
  }, []);

  const refreshCustomersList = useCallback(
    (cursor) => {
      let params = { page_size: 30 };
      if (cursor) {
        const urlObj = new URL(cursor);
        params.cursor = urlObj.searchParams.get("cursor");
      }

      if (search) {
        params.search = search;
      }

      const fetchData = async () => {
        const data = await fetchCustomers(profile, params);
        setCustomerList(data.results);
        setNextCursor(data.next);
        setPreviousCursor(data.previous);
      };
      fetchData();
    },
    [profile, search]
  );

  useEffect(() => {
    refreshCustomersList();
  }, [profile, refreshCustomersList]);

  const handleNextPage = () => {
    if (nextCursor) {
      refreshCustomersList(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    if (previousCursor) {
      refreshCustomersList(previousCursor);
    }
  };

  const handleSubmitLinkClients = (event, linkedCustomers) => {
    event.preventDefault();
    const formData = new FormData();

    linkedCustomers.forEach((customer) => {
      formData.append("customer_group", customer.value);
    });

    if (selectedClient?.customer_group) {
      updateCustomerGroup(
        selectedClient.id,
        selectedClient.customer_group,
        formData
      )
        .then((res) => {
          setCustomerList(
            customerList.map((p) => (p.id === res.data.id ? res.data : p))
          );
          context.fireToast(
            "Success",
            "Account group has been updated.",
            "success"
          );
          onAssignClose();
        })
        .catch((err) => console.log(err));
    } else {
      createCustomerGroup(selectedClient.id, formData)
        .then((res) => {
          const updatedCustomerList = [...customerList];
          const customerToUpdate = updatedCustomerList.find(
            (customer) => customer.id === selectedClient.id
          );

          if (customerToUpdate) {
            customerToUpdate.customer_group = res.data.id;
          }

          setCustomerList(updatedCustomerList);
          context.fireToast(
            "Success",
            "Account group has been updated.",
            "success"
          );
          onAssignClose();
        })
        .catch((err) => console.log(err));
    }
  };

  const handleSubmit = (event, data) => {
    event.preventDefault();

    if (data.id) {
      updateCustomer(data.id, data)
        .then((res) => {
          setCustomerList((prevList) =>
            prevList.map((c) => (c.id === res.data.id ? res.data : c))
          );
          onClientClose();
          context.fireToast("Success", "Account updated.", "success");
        })
        .catch((err) => console.log(err));
    } else {
      createCustomer(data)
        .then((res) => {
          setCustomerList((prevList) => [...prevList, res.data]);
          onClientClose();
          context.fireToast("Success", "Account created.", "success");
        })
        .catch((err) => console.log(err));
    }
  };

  const startCreateClient = () => {
    setSelectedClient(null);
    onClientOpen();
  };

  const startAssignClients = (customer) => {
    setSelectedClient(customer);
    onAssignOpen();
  };

  const startUpdateClient = (customer) => {
    setSelectedClient(customer);
    onClientOpen();
  };

  const startDelete = () => {
    onClientClose();
    onDeleteOpen();
  };

  const removeCustomerFromList = (customerId) => {
    setCustomerList((prevList) => prevList.filter((c) => c.id !== customerId));
    context.fireToast("Success", "Account Deleted.", "success");
  };

  const onExportCustomers = () => {
    setLoading(true);
    exportCustomers()
      .then((response) => {
        const link = document.createElement("a");
        link.href = response.data.file;
        link.setAttribute("download", response.data.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setLoading(false);
      })
      .catch((err) => console.log(err));
  };

  const handleSelectAll = useCallback(() => {
    if (selectedCustomers.length === customerList.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customerList.map((c) => c.id));
    }
  }, [selectedCustomers.length, customerList]);

  const { allSelected, someSelected } = useMemo(() => ({
    allSelected: selectedCustomers.length === customerList.length,
    someSelected: selectedCustomers.length > 0 && selectedCustomers.length < customerList.length
  }), [selectedCustomers.length, customerList.length]);

  const handleSelectCustomer = useCallback((id) => {
    setSelectedCustomers((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  }, []);

  const handleUpdateReportStatus = useCallback((customerId, newStatus) => {
    setCustomerList((prev) =>
      prev.map((cust) =>
        cust.id === customerId
          ? { ...cust, report_status: newStatus }
          : cust
      )
    );
  }, []);

  const handleUpdateDashboardDate = useCallback((customerId, newDate) => {
    setCustomerList((prev) =>
      prev.map((cust) =>
        cust.id === customerId
          ? { ...cust, dashboard_date: newDate }
          : cust
      )
    );
  }, []);

  return (
    <Container maxW="100%" bg="brand.50" minHeight={"100vh"} pt={"80px"} h="100%">
      <Stack direction="column" spacing={2} align="center">
        <CustomTableContainer width="98%">
          <Flex pb="5">
            <Box>
              <Heading as="h3" size="lg">
                Account Listing
              </Heading>
            </Box>
            <Spacer />
            <Box>
              <Stack direction="row" spacing={6} align="center">
                <CustomerDelete
                  isOpen={isDeleteOpen}
                  onClose={onDeleteClose}
                  customer={selectedClient}
                  removeCustomerFromList={removeCustomerFromList}
                />
                <CustomerModal
                  isOpen={isClientOpen}
                  onClose={onClientClose}
                  customer={selectedClient}
                  handleSubmit={handleSubmit}
                  startDelete={startDelete}
                />

                <CustomerAssignModal
                  isOpen={isAssignOpen}
                  onClose={onAssignClose}
                  customer={selectedClient}
                  loading={loading}
                  handleSubmit={handleSubmitLinkClients}
                />
                {profile?.type === "admin" && (
                  <>
                    <Button onClick={onExportCustomers}>Export Accounts</Button>
                  </>
                )}
                <Button leftIcon={<AddIcon />} onClick={startCreateClient}>
                  Create Account
                </Button>
                <SearchInput onSearchChange={handleSearchChange} />
                <Box mb={4} display="flex" gap="12px" alignItems="center"></Box>
              </Stack>
              {selectedCustomers?.length !== 0 && (
                <Box
                  mt={4}
                  display="flex"
                  gap="12px"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Text>Date:</Text>
                  <BulkCalendarComponent
                    selectedIds={selectedCustomers}
                    customerList={customerList}
                    setCustomerList={setCustomerList}
                  />
                  <Text>Report Status:</Text>
                  <BulkReportStatusSelect
                    selectedIds={selectedCustomers}
                    customerList={customerList}
                    setCustomerList={setCustomerList}
                  />
                </Box>
              )}
            </Box>
          </Flex>
          <Box maxHeight="65vh" bg="white" overflowX="auto">
            <Table variant="unstyled" width="100%">
              <Thead position="sticky" top={-1} zIndex={1}>
                <Tr>
                  <Th>
                    <Checkbox
                      isChecked={allSelected}
                      isIndeterminate={someSelected}
                      onChange={handleSelectAll}
                    />
                  </Th>
                  <Th>Business Name</Th>
                  <Th>Email Address</Th>
                  <Th>Primary Contact</Th>
                  <Th>Report Status</Th>
                  <Th>Dashboard Date</Th>
                  <Th>Current Status</Th>
                  <Th style={{ width: "118px" }}>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {customerList.map((customer) => (
                  <CustomerRow
                    key={customer.id}
                    customer={customer}
                    isSelected={selectedCustomers.includes(customer.id)}
                    onSelectCustomer={handleSelectCustomer}
                    onUpdateReportStatus={handleUpdateReportStatus}
                    onUpdateDashboardDate={handleUpdateDashboardDate}
                    onStartAssignClients={startAssignClients}
                    onStartUpdateClient={startUpdateClient}
                  />
                ))}
              </Tbody>
            </Table>
          </Box>
          <Stack direction="row" spacing={4} mt={4} justifyContent="center">
            {previousCursor && (
              <Button onClick={handlePreviousPage}>Previous</Button>
            )}
            {nextCursor && (
              <Button onClick={handleNextPage} disabled={!nextCursor}>
                Next
              </Button>
            )}
          </Stack>
        </CustomTableContainer>
      </Stack>
    </Container>
  );
};

const CustomerModal = ({
  isOpen,
  onClose,
  customer,
  loading,
  handleSubmit,
  startDelete,
}) => {
  const [clientData, setClientData] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  const text = !customer?.id ? "Create" : "Update";

  const clientTypeOptions = [
    { value: "normal", label: "Normal" },
    { value: "group", label: "Group" },
    { value: "trial", label: "Trial" },
  ];

  useEffect(() => {
    setImagePreview(null);
    if (customer?.logo) {
      setImagePreview(customer.logo);
      delete customer.logo;
    }
    setClientData(customer);
  }, [customer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientData({ ...clientData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setClientData({ ...clientData, [e.target.name]: file });
    setImagePreview(URL.createObjectURL(file));
  };

  const [selectedClientType, setSelectedClientType] = useState(
    clientData?.customer_type ? clientData.customer_type : "normal"
  );

  useEffect(() => {
    setSelectedClientType(
      clientData?.customer_type ? clientData.customer_type : "normal"
    );
  }, [customer, isOpen, clientData?.customer_type]);

  const handleClientTypeChange = (option) => {
    setSelectedClientType(option.value);
    setClientData({ ...clientData, customer_type: option.value });
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${text} Account`}
      loading={loading}
      body={
        <form
          id="customerForm"
          onSubmit={(e) => handleSubmit(e, clientData)}
          encType="multipart/form-data"
        >
          <FormControl id="name" isRequired>
            <FormLabel>Name</FormLabel>
            <Input
              type="text"
              name="name"
              id="clientName"
              value={clientData?.name || ""}
              onChange={handleInputChange}
            />
          </FormControl>
          <FormControl id="email" isRequired>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              name="email"
              id="clientEmail"
              value={clientData?.email || ""}
              onChange={handleInputChange}
            />
          </FormControl>
          <FormControl id="primary_contact">
            <FormLabel>Primary Contact</FormLabel>
            <Input
              type="text"
              name="primary_contact"
              id="clientPrimaryContact"
              value={clientData?.primary_contact || ""}
              onChange={handleInputChange}
            />
          </FormControl>
            <FormControl>
              <FormLabel>Account Type</FormLabel>
              <DropDown
                name="customer_type"
                options={clientTypeOptions}
                selectedValue={selectedClientType}
                onChange={handleClientTypeChange}
                w="100%"
              />
            </FormControl>

          <LogoField
            name="logo"
            text="Account Logo"
            imagePreview={imagePreview}
            onChange={handleImageChange}
          />
        </form>
      }
      leftButton={
        customer?.id && (
          <Button onClick={startDelete} colorScheme="red">
            Delete
          </Button>
        )
      }
      submitButton={
        <Button ml="3" type="submit" form="customerForm">
          {text}
        </Button>
      }
    />
  );
};

const CustomerDelete = ({
  isOpen,
  onClose,
  customer,
  removeCustomerFromList,
}) => {
  const context = React.useContext(Context);
  const [loading, setLoading] = useState(false);

  const handleDelete = () => {
    setLoading(true);
    deleteCustomer(customer.id)
      .then((response) => {
        if (response.status === 200) {
          removeCustomerFromList(customer.id);
          onClose();
        } else {
          context.fireToast(
            "Error",
            response["request"]["responseText"],
            "error"
          );
        }
      })
      .catch((error) => {
        console.error("There was an error deleting the client!", error);
        context.fireToast("Error", "Failed to delete the account.", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={"Confirm Delete"}
      loading={loading}
      body={`Are you sure you want to delete ${customer?.name}?`}
      submitButton={
        <Button
          colorScheme="red"
          ml="3"
          onClick={handleDelete}
          disabled={loading}
        >
          {loading ? "Processing..." : "Delete"}
        </Button>
      }
    />
  );
};

const CustomerAssignModal = ({
  isOpen,
  onClose,
  customer,
  loading,
  handleSubmit,
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [linkedCustomers, setLinkedCustomers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const paramsLinked = { customer_id: customer?.id, exclude_group: false };
      const linkedResults = fetchGroupCustomers(paramsLinked, true);

      const paramsUnlinked = {
        customer_id: customer?.id,
        exclude_group: true,
      };
      const unlinkedResults = fetchGroupCustomers(paramsUnlinked, true);

      setLinkedCustomers((await linkedResults) ?? []);
      setCustomers((await unlinkedResults) ?? []);
    };
    fetchData();
  }, [isOpen, customer]);

  const handleSelectCustomer = (customer) => {
    setCustomers((prev) => prev.filter((c) => c.value !== customer.value));
    setLinkedCustomers([...linkedCustomers, customer]);
    setSelectedCustomer(null);
  };

  const removeCustomer = (customer) => {
    setLinkedCustomers((prev) =>
      prev.filter((c) => c.value !== customer.value)
    );
    setCustomers([...customers, customer]);
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Account Group"
      submitButton={
        <Button
          type="submit"
          form="updateCustomerGroup"
          isDisabled={loading}
          ml={3}
        >
          {loading ? <Spinner size="sm" /> : "Save"}
        </Button>
      }
      body={
        <form
          id={"updateCustomerGroup"}
          onSubmit={(e) => handleSubmit(e, linkedCustomers)}
        >
          <FormControl>
            <FormLabel htmlFor="customer">Add Account</FormLabel>
            <DropDown
              name="customer"
              options={customers}
              selectedValue={selectedCustomer}
              onChange={handleSelectCustomer}
              isRequired={false}
            />
          </FormControl>

          <Box
            mt={5}
            maxHeight="calc(100vh - 480px)"
            bg="white"
            overflowX="auto"
          >
            <Table variant="unstyled" size="sm">
              <Thead>
                <Tr>
                  <Td>Linked Accounts</Td>
                  <Td></Td>
                </Tr>
              </Thead>
              <Tbody>
                {linkedCustomers?.map((c) => (
                  <Tr key={c.value}>
                    <Td pt="4px" pb="4px">
                      {c.label}
                    </Td>
                    <Td pt="4px" pb="4px" w="40px">
                      <IconButton
                        colorScheme="red"
                        size="sm"
                        icon={<Trash />}
                        onClick={() => removeCustomer(c)}
                        variant="outline"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </form>
      }
    />
  );
};
